import { debugLog } from '@schibsted-nmp/advertising-shared';

/** Based on {@link https://github.schibsted.io/spt-privacy/sourcepoint/blob/master/src/browser/api/index.js} */
export namespace TcfHelperMock {
  export type ConsentState = 'Consented' | 'NonConsented';

  export namespace ConsentState {
    let currentValue: ConsentState = 'Consented';

    export function get() {
      return currentValue;
    }

    export function set(consentState: ConsentState) {
      currentValue = consentState;
      trySetConsentCookies(consentState);
    }

    /**
     * Tries to set the consent cookies based on the current consent state. This
     * is intended as a mock implementation and should only be used in conjunction
     * with {@linkcode TcfHelperMock} on local development environments. These
     * mock cookies live throughout the session only.
     *
     * The consent cookies covered are:
     * - `_cmp_advertising`
     * - `_cmp_analytics`
     * - `_cmp_marketing`
     * - `_cmp_personalisation`
     *
     * **_Note:_**
     * Real cookie equivalents are deleted before setting the mock cookies
     * to avoid conflicts with consent cookies carried over from other environments.
     */
    export function trySetConsentCookies(consentState: ConsentState) {
      try {
        const cookieValue = consentState === 'Consented' ? '1' : '0';

        const realCookieDomain = window.location.hostname.replace(
          'local.',
          '.'
        );

        for (const cookieName of trySetConsentCookies.names) {
          // Delete real cookie to avoid conflicts from other environments:
          document.cookie = `_cmp_${cookieName}=; domain=${realCookieDomain}; max-age=0;`;

          // Create mock cookie that only applies locally:
          document.cookie = `_cmp_${cookieName}=${cookieValue}; path=/;`;
        }
      } catch (error) {
        debugLog('Failed to set mock consent cookies', error);
      }
    }

    trySetConsentCookies.names = [
      'advertising',
      'analytics',
      'marketing',
      'personalisation'
    ] as const;
  }

  export namespace Window {
    export function inject() {
      if (window._tcf_) return;

      const consentState =
        sessionStorage.getItem('ADVT_DEV_TOOL_consentState') === 'NonConsented'
          ? 'NonConsented'
          : 'Consented';

      ConsentState.set(consentState);

      window._tcf_ = {
        getConsentedToAllSync() {
          return ConsentState.get() === 'Consented';
        },

        isConsentedToAll(callback) {
          callback(ConsentState.get() === 'Consented');
        }
      };
    }
  }
}
